import get from 'lodash-es/get';
import set from 'lodash-es/set';

import {
  getOwnerData,
  getQuotation,
  getQuotationIsFromRenewal,
  setDataOwnersChangedRenewal,
  setMessageVisibility,
  getFormValues,
  setMainOwnerSelectionData,
  getMainOwnerSelectionData,
  getFormConfig,
  reinitializeForm,
  changeFieldValueAndUpdate,
} from 'app/services/businessFunctionHelpers';
import { setBirthdateAndGenderByPesel } from 'app/services/businessFunctions/helpers/setBirthdateAndGenderByPesel';
import {
  BusinessFunctionsService,
  setNoDriverLicenseFieldsVisibility,
} from 'app/services/businessFunctions.service';
import { setFamilyNameVisibility } from 'app/services/businessFunctions/helpers/setFamilyNameVisibility';
import { isPeselValid, stringifyPesel } from 'app/services/pesel.service';
import { getMessageKeyWithSuffix } from 'app/services/validation.service';
import { checkHasDataChangedForRenewal } from 'app/services/businessFunctions/helpers/checkHasDataChangedForRenewal';
import { QuotationService } from 'app/services/quotation.service';
import { revalidatePeselsUniqueness } from 'app/services/pesel.service/revalidatePeselsUniqueness';
import {
  DriverType,
  FieldsEnum,
  FormsEnum,
  MainOwnerSelectionEnum,
  SectionsEnum,
} from 'app/shared/enums';
import {
  CalculationDataByKeysInterface,
  Coowner,
} from 'app/shared/interfaces/CalculationDataByKeys.interface';
import { JsonPatchValueType } from 'app/shared/types/JsonPatchValue.type';

const ownerOnChange = {
  ownerPeselChange(initialRun: boolean) {
    const { mainOwner } = getQuotation();
    const isFromRenewal = getQuotationIsFromRenewal();
    const { pesel } = getOwnerData();
    const previousPesel = mainOwner?.peselNumber || '';
    const isValidPesel = isPeselValid(stringifyPesel(pesel));

    if (initialRun) {
      setMessageVisibility({
        formName: FormsEnum.CalculationForm,
        key: getMessageKeyWithSuffix(FieldsEnum.OwnerPesel, 'invalid_pesel'),
        visible: false,
      });

      setMessageVisibility({
        formName: FormsEnum.CalculationForm,
        key: getMessageKeyWithSuffix(
          FieldsEnum.OwnerPesel,
          'too_young_owner_pesel'
        ),
        visible: false,
      });
    }

    setBirthdateAndGenderByPesel(
      initialRun,
      pesel as number,
      FormsEnum.CalculationForm,
      FieldsEnum.OwnerBirthDate,
      FieldsEnum.OwnerGender
    );

    setFamilyNameVisibility(
      FieldsEnum.OwnerFamilyName,
      FormsEnum.CalculationForm,
      stringifyPesel(pesel),
      isValidPesel
    );

    if (isFromRenewal) {
      if (checkHasDataChangedForRenewal(pesel, previousPesel)) {
        setDataOwnersChangedRenewal({
          ownerName: 'owner',
          isChanged: true,
        });

        if (!initialRun && isValidPesel) {
          BusinessFunctionsService.setupOwnerLicenseDates(initialRun);
          setNoDriverLicenseFieldsVisibility(DriverType.OWNER, initialRun);
        }
      }
    } else if (initialRun || isValidPesel) {
      BusinessFunctionsService.setupOwnerLicenseDates(initialRun);
      setNoDriverLicenseFieldsVisibility(DriverType.OWNER, initialRun);
    }
  },

  mainOwnerSelectionChange() {
    const { id: quotationId } = getQuotation();
    const formValues = getFormValues();
    const formConfig = getFormConfig(FormsEnum.CalculationForm);
    const {
      mainOwnerSelection,
      owner: mainOwnerValues,
      coowners: [, coownerValues],
    } = formValues;
    const previousMainOwnerSelection =
      mainOwnerSelection === MainOwnerSelectionEnum.MainOwner
        ? MainOwnerSelectionEnum.Coowner1
        : MainOwnerSelectionEnum.MainOwner;
    const mainOwnerSelectionData = getMainOwnerSelectionData();
    const ownersData = {
      [MainOwnerSelectionEnum.MainOwner]: {
        ...mainOwnerSelectionData[MainOwnerSelectionEnum.MainOwner],
      },
      [MainOwnerSelectionEnum.Coowner1]: {
        ...mainOwnerSelectionData[MainOwnerSelectionEnum.Coowner1],
      },
    };
    const newOwnerData = {} as CalculationDataByKeysInterface['owner'];
    const newCoownerData = {} as CalculationDataByKeysInterface['coowners'][1];
    const ownerQuestionsWithDelayNeeded = new Array<{
      key: string;
      val: JsonPatchValueType;
    }>();
    const ownerRestQuestions = new Array<{
      key: string;
      val: JsonPatchValueType;
    }>();
    const coownerQuestionsWithDelayNeeded = new Array<{
      key: string;
      val: JsonPatchValueType;
    }>();
    const coownerRestQuestions = new Array<{
      key: string;
      val: JsonPatchValueType;
    }>();
    const questionsWithDelayNeeded = [
      'legalAddress',
      'icenseYear',
      'icenseMonth',
      'maritalStatus',
      'noDrivingLicense',
      'hasChildren',
      'oldestChildBirthYear',
      'homeAddress',
      'familyName',
    ];

    Object.keys(mainOwnerValues).forEach(
      (key: keyof CalculationDataByKeysInterface['owner']) => {
        ownersData[previousMainOwnerSelection] = {
          ...ownersData[previousMainOwnerSelection],
          [key]: mainOwnerValues[key],
        };
      }
    );

    Object.keys(coownerValues).forEach((key: keyof Coowner) => {
      ownersData[mainOwnerSelection!] = {
        ...ownersData[mainOwnerSelection!],
        [key]: coownerValues[key],
      };
    });

    setMainOwnerSelectionData(ownersData);

    formConfig.sections[SectionsEnum.Owner].questions.forEach(
      (path: string) => {
        if (path.includes('coownersCount')) return;

        let incomingPath, outgoingPath;

        if (path.includes('driverLicenseMonth')) {
          incomingPath = 'licenseMonth' as keyof CalculationDataByKeysInterface['owner'];
          outgoingPath = 'driverLicenseMonth' as keyof CalculationDataByKeysInterface['owner'];
        } else if (path.includes('driverLicenseYear')) {
          incomingPath = 'licenseYear' as keyof CalculationDataByKeysInterface['owner'];
          outgoingPath = 'driverLicenseYear' as keyof CalculationDataByKeysInterface['owner'];
        } else {
          incomingPath = outgoingPath = path.split(
            'owner.'
          )[1] as keyof CalculationDataByKeysInterface['owner'];
        }

        const storedValue = get(
          ownersData[mainOwnerSelection!],
          incomingPath,
          ''
        ) as JsonPatchValueType;

        if (questionsWithDelayNeeded.some((el) => path.includes(el))) {
          ownerQuestionsWithDelayNeeded.push({
            key: outgoingPath,
            val: storedValue,
          });
        } else {
          ownerRestQuestions.push({ key: outgoingPath, val: storedValue });
        }

        set(newOwnerData, outgoingPath, storedValue);
      }
    );

    formConfig.sections[SectionsEnum.Coowners1].questions.forEach(
      (path: keyof CalculationDataByKeysInterface['coowners'][1]) => {
        let incomingPath, outgoingPath;

        if (path.includes('licenseMonth')) {
          incomingPath = 'driverLicenseMonth' as keyof CalculationDataByKeysInterface['coowners'][1];
          outgoingPath = 'licenseMonth' as keyof CalculationDataByKeysInterface['coowners'][1];
        } else if (path.includes('licenseYear')) {
          incomingPath = 'driverLicenseYear' as keyof CalculationDataByKeysInterface['coowners'][1];
          outgoingPath = 'licenseYear' as keyof CalculationDataByKeysInterface['coowners'][1];
        } else {
          incomingPath = outgoingPath = path.split(
            'coowners.1.'
          )[1] as keyof CalculationDataByKeysInterface['coowners'][1];
        }

        const storedValue = get(
          ownersData[previousMainOwnerSelection],
          incomingPath,
          ''
        ) as JsonPatchValueType;

        if (questionsWithDelayNeeded.some((el) => path.includes(el))) {
          coownerQuestionsWithDelayNeeded.push({
            key: outgoingPath,
            val: storedValue,
          });
        } else {
          coownerRestQuestions.push({ key: outgoingPath, val: storedValue });
        }

        set(newCoownerData, outgoingPath, storedValue);
      }
    );

    reinitializeForm(FormsEnum.CalculationForm, {
      ...formValues,
      owner: newOwnerData,
      coowners: [
        formValues.coowners[0],
        newCoownerData,
        formValues.coowners[2],
      ],
    });

    ownerQuestionsWithDelayNeeded.forEach(({ key, val }) => {
      setTimeout(() => {
        changeFieldValueAndUpdate(`owner.${key}`, val);
      });
    });

    coownerQuestionsWithDelayNeeded.forEach(({ key, val }) => {
      setTimeout(() => {
        changeFieldValueAndUpdate(`coowners.1.${key}`, val);
      });
    });

    ownerRestQuestions.forEach(({ key, val }) => {
      const k = key.replace('pesel', 'peselNumber');
      setTimeout(() =>
        QuotationService.update(quotationId, `mainOwner.${k}`, val)
      );
    });

    coownerRestQuestions.forEach(({ key, val }) => {
      const k = key.replace('pesel', 'peselNumber');
      setTimeout(() =>
        QuotationService.update(quotationId, `coOwners.0.${k}`, val)
      );
    });

    setTimeout(() => {
      BusinessFunctionsService.ownerChildrenCountChange();
      BusinessFunctionsService.ownerHomeAddressNotSameAsLegalChange();
      revalidatePeselsUniqueness(FormsEnum.CalculationForm);
      BusinessFunctionsService.setupOwnerLicenseDates(true);
      BusinessFunctionsService.setupFirstCoownerLicenseDates(true);
      BusinessFunctionsService.setOwnerLicenseMonthVisibility();
      BusinessFunctionsService.setFirstCoownerLicenseMonthVisibility();
    });
  },
};

export default ownerOnChange;
